/* ===========================================================================
 * Aires Infrastructure Map — section JS.
 *
 * Multi-instance, lazy-init (IntersectionObserver), per-section state.
 * Read settings from each <script type="application/json"
 *   data-infra-map-config="<sectionId>"> block emitted by the section Liquid.
 *
 * Loaded with `defer`, runs after Mapbox GL JS + the geocoder plugin (both
 * also `defer`-loaded, same execution order). Falls back gracefully if a
 * dependency is missing.
 *
 * Layers:
 *   1. fcc_5g_coverage     — Esri Living Atlas vector tile service (free)
 *   2. transmission_lines  — <mb-user>.transmission_lines  (LineString)
 *   3. electric_substations— <mb-user>.electric_substations (Point)
 *   4. wireless_towers     — <mb-user>.wireless_towers     (Point)
 *   5. power_plants        — <mb-user>.power_plants        (Point)
 *
 * Stack order: 5G under everything else so towers/lines stay readable on top.
 * =========================================================================== */
(function () {
  "use strict";

  // External vector tile source for FCC 5G coverage. Public, CDN-cached.
  // Filter on Esri's pre-classified `_symbol` codes (0/1=5G mid-band 35/3,
  // 2/3=5G low-band 7/1, 4/5=4G LTE only, 6=no service).
  var FCC_5G_TILE_URL =
    "https://vectortileservices.arcgis.com/jIL9msH9OI208GCb/arcgis/rest/services/" +
    "FCC_Mobile_Broadband_Data_Collection/VectorTileServer/tile/{z}/{y}/{x}.pbf";

  // The layers we own (everything but the 5G overlay), in *rendering* order:
  // first entry is drawn first (bottom), last entry on top.
  var OWN_LAYERS = [
    {
      id: "transmission_lines",
      type: "line",
      geometryType: "line",
    },
    {
      id: "electric_substations",
      type: "circle",
      geometryType: "point",
    },
    {
      id: "wireless_towers",
      type: "circle",
      geometryType: "point",
    },
    {
      id: "power_plants",
      type: "circle",
      geometryType: "point",
    },
  ];

  var ALL_LAYER_IDS = ["fcc_5g_coverage"].concat(OWN_LAYERS.map(function (l) { return l.id; }));

  var LAYER_LABEL = {
    fcc_5g_coverage:      "5G coverage (FCC)",
    wireless_towers:      "Wireless towers",
    electric_substations: "Electric substations",
    transmission_lines:   "Transmission lines",
    power_plants:         "Power plants",
  };

  // Built-in Mapbox styles offered in the basemap switcher (if enabled).
  var BASEMAP_OPTIONS = [
    { value: "mapbox://styles/mapbox/light-v11",            label: "Light" },
    { value: "mapbox://styles/mapbox/dark-v11",             label: "Dark" },
    { value: "mapbox://styles/mapbox/streets-v12",          label: "Streets" },
    { value: "mapbox://styles/mapbox/outdoors-v12",         label: "Outdoors" },
    { value: "mapbox://styles/mapbox/satellite-v9",         label: "Satellite" },
    { value: "mapbox://styles/mapbox/satellite-streets-v12",label: "Satellite + Streets" },
    { value: "mapbox://styles/mapbox/standard",             label: "Standard (3D)" },
  ];

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "class") node.className = attrs[k];
        else if (k === "style" && typeof attrs[k] === "object") Object.assign(node.style, attrs[k]);
        else if (k.indexOf("on") === 0 && typeof attrs[k] === "function") node.addEventListener(k.slice(2), attrs[k]);
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
    });
    return node;
  }

  function rowsToHtml(p) {
    var fields = [
      ["Name", p.name],
      ["Owner / operator", p.owner != null ? p.owner : p.operator],
      ["Voltage", p.voltage_kv != null ? p.voltage_kv + " kV" : null],
      ["Capacity", p.capacity_mw != null ? p.capacity_mw + " MW" : null],
      ["Fuel", p.primary_fuel],
      ["Technology", p.technology],
      ["Height", p.height_m != null ? p.height_m + " m" : null],
      ["Structure", p.structure_type],
      ["Type", p.type],
      ["Status", p.status],
      ["State", p.state],
      ["City / County", p.city || p.county],
      ["Source", p.source],
      ["ID", p.id],
    ].filter(function (f) { return f[1] != null && f[1] !== ""; });
    return fields.map(function (f) {
      return '<div><span style="color:#6c757d">' + f[0] + ":</span> " + String(f[1]) + "</div>";
    }).join("");
  }

  // -------------------------------------------------------------------------
  function AiresInfraMap(config) {
    this.config = config;
    this.container = document.getElementById(config.containerId);
    if (!this.container) {
      console.warn("[infra-map] container missing:", config.containerId);
      return;
    }

    // Per-instance state — visibility map mirrors the merchant defaults and
    // is then mutated by the layer-toggle UI (if visitor is allowed to change).
    this.visibility = Object.assign({}, config.defaultLayers);
    this.currentBasemap = config.basemap;
    this.map = null;
    this.initialized = false;

    // Apply per-breakpoint height as a CSS var on the container.
    this.applyHeight();
    window.addEventListener("resize", this.applyHeight.bind(this));

    this.observeForLazyInit();
  }

  AiresInfraMap.prototype.applyHeight = function () {
    var isMobile = window.matchMedia("(max-width: 749px)").matches;
    this.container.style.height = (isMobile ? this.config.heightMobile : this.config.heightDesktop) + "px";
  };

  // Lazy-init: don't create the map until the container is in the viewport.
  // For sections used above the fold, this still fires on first paint.
  AiresInfraMap.prototype.observeForLazyInit = function () {
    var self = this;
    if (!("IntersectionObserver" in window)) {
      this.init();
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting && !self.initialized) {
          self.initialized = true;
          io.disconnect();
          self.init();
        }
      });
    }, { rootMargin: "200px 0px" }); // start loading ~200px before scroll-in
    io.observe(this.container);
  };

  AiresInfraMap.prototype.init = function () {
    if (typeof mapboxgl === "undefined") {
      this.container.innerHTML =
        '<div style="padding:1rem;color:#c92a2a">Mapbox GL JS failed to load.</div>';
      return;
    }
    if (!this.config.token || !this.config.mapboxUsername) {
      this.container.innerHTML =
        '<div style="padding:1rem;color:#c92a2a">Mapbox token or username not set in Theme settings.</div>';
      return;
    }

    mapboxgl.accessToken = this.config.token;
    this.map = new mapboxgl.Map({
      container: this.config.containerId,
      style: this.currentBasemap,
      center: this.config.center,
      zoom: this.config.zoom,
      // Cooperative gestures: one-finger drag scrolls the page (mobile),
      // ctrl/⌘ + scroll-wheel zooms (desktop). Prevents the map from
      // hijacking page scroll. Mapbox shows an automatic overlay hint
      // ("Use two fingers to move the map") on first interaction.
      cooperativeGestures: true,
    });

    this.addControls();
    this.addOverlayPanel();
    this.map.on("style.load", this.addInfraLayers.bind(this));
    this.map.on("error", function (e) { console.error("[infra-map]", e); });
  };

  // -------------------------------- CONTROLS --------------------------------
  // Mapbox stacks multiple controls at the same position in the order they're
  // added. Search is the primary action so it goes FIRST (top of the stack),
  // then zoom, then geolocate below it.
  AiresInfraMap.prototype.addControls = function () {
    var c = this.config.controls;
    if (c.geocoder && c.geocoder.show && typeof MapboxGeocoder !== "undefined") {
      this.map.addControl(new MapboxGeocoder({
        accessToken: this.config.token,
        mapboxgl: mapboxgl,
        marker: { color: "#1864ab" },
        // No `countries` filter — the infrastructure tilesets are global
        // (OpenCelliD, OSM world, WRI, etc.). Restricting the geocoder to
        // the US silently downgrades anyone typing an international
        // address into a fuzzy US match.
        placeholder: "Search address, city, or postal code…",
        zoom: 13,
        flyTo: { speed: 1.4 },
      }), "top-right");
    }
    if (c.zoom && c.zoom.show) {
      this.map.addControl(new mapboxgl.NavigationControl({ showZoom: true, showCompass: true }), "top-right");
    }
    if (c.geolocate && c.geolocate.show) {
      this.map.addControl(new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
      }), "top-right");
    }
    if (c.scale && c.scale.show) {
      this.map.addControl(new mapboxgl.ScaleControl({ maxWidth: 120, unit: "imperial" }), "bottom-left");
    }
  };

  // ----------------------------- OVERLAY PANEL ------------------------------
  // A single absolutely-positioned <div> inside the container holding the
  // layer-toggle list and the basemap switcher. Skipped entirely if both
  // controls are hidden.
  //
  // Mobile UX: panel collapses to a single 44x44 "layers" icon button by
  // default; tapping the button toggles the .is-open class which the CSS
  // uses to reveal the panel body. Desktop CSS keeps the body visible
  // regardless of state, so the toggle is effectively no-op there.
  AiresInfraMap.prototype.addOverlayPanel = function () {
    var c = this.config.controls;
    var showToggles = c.layerToggles.show;
    var showBasemap = c.basemapSwitcher.show;
    if (!showToggles && !showBasemap) return;

    var panel = el("div", { class: "infra-map__panel", "data-section-id": this.config.sectionId });

    // Collapse/expand button (visible on mobile only via CSS).
    var toggleBtn = el("button", {
      type: "button",
      class: "infra-map__panel-toggle",
      "aria-label": "Toggle map layers panel",
      "aria-expanded": "false",
    });
    // Material "layers" icon — stacked squares.
    toggleBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>' +
      "</svg>";
    toggleBtn.addEventListener("click", function () {
      var open = panel.classList.toggle("is-open");
      toggleBtn.setAttribute("aria-expanded", String(open));
    });
    panel.appendChild(toggleBtn);

    // Everything else lives in the body — on mobile we hide/show this; on
    // desktop the CSS leaves it visible unconditionally.
    var body = el("div", { class: "infra-map__panel-body" });

    if (showToggles) {
      var allow = c.layerToggles.allowChange;
      body.appendChild(el("div", { class: "infra-map__panel-title" }, ["Layers"]));
      var self = this;
      ALL_LAYER_IDS.forEach(function (layerId) {
        var checked = !!self.visibility[layerId];
        var input = el("input", {
          type: "checkbox",
          "data-layer": layerId,
        });
        input.checked = checked;
        if (!allow) input.disabled = true;
        input.addEventListener("change", function () {
          self.visibility[layerId] = input.checked;
          if (self.map.getLayer(layerId)) {
            self.map.setLayoutProperty(layerId, "visibility", input.checked ? "visible" : "none");
          }
        });
        body.appendChild(
          el("label", { class: "infra-map__toggle" + (allow ? "" : " is-locked") }, [
            input,
            el("span", { class: "infra-map__swatch", "data-layer": layerId }),
            el("span", null, [LAYER_LABEL[layerId]]),
          ])
        );
      });
    }

    if (showBasemap) {
      var allowBasemap = c.basemapSwitcher.allowChange;
      body.appendChild(el("div", { class: "infra-map__panel-title", style: { marginTop: "10px" } }, ["Basemap"]));
      var select = el("select", { class: "infra-map__basemap-select" });
      if (!allowBasemap) select.disabled = true;
      var hasCurrent = false;
      BASEMAP_OPTIONS.forEach(function (opt) {
        var o = el("option", { value: opt.value }, [opt.label]);
        if (opt.value === this.currentBasemap) { o.selected = true; hasCurrent = true; }
        select.appendChild(o);
      }.bind(this));
      // If merchant set a custom Mapbox Studio style not in the preset list,
      // add it so the dropdown still reflects the current selection.
      if (!hasCurrent) {
        var custom = el("option", { value: this.currentBasemap }, ["Custom"]);
        custom.selected = true;
        select.appendChild(custom);
      }
      var selfRef = this;
      select.addEventListener("change", function (e) {
        selfRef.currentBasemap = e.target.value;
        selfRef.map.setStyle(selfRef.currentBasemap); // triggers style.load → re-adds layers
      });
      body.appendChild(select);
    }

    panel.appendChild(body);
    this.container.appendChild(panel);
  };

  // -------------------------------- LAYERS ----------------------------------
  AiresInfraMap.prototype.addInfraLayers = function () {
    var user = this.config.mapboxUsername;
    var colors = this.config.colors;
    var vis = this.visibility;
    var m = this.map;

    // ----- FCC 5G COVERAGE (Esri Living Atlas) — bottom layer -----
    m.addSource("fcc_5g_coverage", {
      type: "vector",
      tiles: [FCC_5G_TILE_URL],
      minzoom: 0,
      maxzoom: 14,
    });
    m.addLayer({
      id: "fcc_5g_coverage",
      type: "fill",
      source: "fcc_5g_coverage",
      "source-layer": "FABRIC",
      filter: ["in", ["get", "_symbol"], ["literal", [0, 1, 2, 3]]],
      layout: { visibility: vis.fcc_5g_coverage ? "visible" : "none" },
      paint: {
        "fill-color": [
          "match", ["get", "_symbol"],
          0, "#065220", // 5G NR 35/3 In-Vehicle (mid-band, fastest)
          1, "#006D2C", // 5G NR 35/3 Outdoor Stationary
          2, "#238B45", // 5G NR 7/1 In-Vehicle  (low-band)
          3, "#41AB5D", // 5G NR 7/1 Outdoor Stationary
          "#94d82d",
        ],
        "fill-opacity": 0.5,
        "fill-outline-color": "rgba(0,0,0,0)",
      },
    });

    // ----- TRANSMISSION LINES -----
    m.addSource("transmission_lines", { type: "vector", url: "mapbox://" + user + ".transmission_lines" });
    m.addLayer({
      id: "transmission_lines",
      type: "line",
      source: "transmission_lines",
      "source-layer": "transmission_lines",
      layout: { visibility: vis.transmission_lines ? "visible" : "none" },
      paint: {
        "line-color": [
          "case",
          [">=", ["coalesce", ["get", "voltage_kv"], 0], 345], "#7c2d12",
          [">=", ["coalesce", ["get", "voltage_kv"], 0], 115], colors.transmission_lines,
          "#fa5252",
        ],
        "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.5, 12, 2.5, 16, 4],
        "line-opacity": 0.8,
      },
    });

    // ----- SUBSTATIONS -----
    m.addSource("electric_substations", { type: "vector", url: "mapbox://" + user + ".electric_substations" });
    m.addLayer({
      id: "electric_substations",
      type: "circle",
      source: "electric_substations",
      "source-layer": "electric_substations",
      layout: { visibility: vis.electric_substations ? "visible" : "none" },
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 7, 2, 13, 6, 16, 10],
        "circle-color": colors.electric_substations,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
        "circle-opacity": 0.9,
      },
    });

    // ----- WIRELESS TOWERS -----
    m.addSource("wireless_towers", { type: "vector", url: "mapbox://" + user + ".wireless_towers" });
    m.addLayer({
      id: "wireless_towers",
      type: "circle",
      source: "wireless_towers",
      "source-layer": "wireless_towers",
      layout: { visibility: vis.wireless_towers ? "visible" : "none" },
      paint: {
        "circle-radius": ["interpolate", ["linear"], ["zoom"], 7, 2, 13, 5, 16, 8],
        "circle-color": colors.wireless_towers,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#fff",
        "circle-opacity": 0.85,
      },
    });

    // ----- POWER PLANTS — sized by capacity, colored by fuel -----
    m.addSource("power_plants", { type: "vector", url: "mapbox://" + user + ".power_plants" });
    m.addLayer({
      id: "power_plants",
      type: "circle",
      source: "power_plants",
      "source-layer": "power_plants",
      layout: { visibility: vis.power_plants ? "visible" : "none" },
      paint: {
        "circle-radius": [
          "interpolate", ["linear"], ["coalesce", ["get", "capacity_mw"], 1],
          0, 3, 100, 6, 500, 10, 1500, 16,
        ],
        "circle-color": [
          "match", ["coalesce", ["get", "primary_fuel"], "OTHER"],
          "NUCLEAR",    "#5f3dc4",
          "COAL",       "#212529",
          "GAS",        colors.power_plants,
          "OIL",        "#495057",
          "SOLAR",      "#fab005",
          "WIND",       "#4dabf7",
          "HYDRO",      "#1c7ed6",
          "BIOMASS",    "#74b816",
          "GEOTHERMAL", "#c64b1d",
          "BATTERY",    "#37b24d",
          "#868e96"
        ],
        "circle-stroke-width": 1.5,
        "circle-stroke-color": "#fff",
        "circle-opacity": 0.9,
      },
    });

    if (this.config.controls.popups && this.config.controls.popups.show) {
      this.bindPopups();
    }

    // Paint each toggle swatch in the panel using its layer's primary color.
    this.colorizePanelSwatches();
  };

  // -------------------------------- POPUPS ----------------------------------
  AiresInfraMap.prototype.bindPopups = function () {
    var m = this.map;
    var popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true });
    OWN_LAYERS.forEach(function (l) {
      m.on("click", l.id, function (e) {
        var f = e.features && e.features[0];
        if (!f) return;
        popup.setLngLat(e.lngLat).setHTML(
          '<div style="font:13px/1.4 system-ui,sans-serif;min-width:220px">' +
            '<div style="font-weight:600;margin-bottom:4px;text-transform:capitalize">' +
              l.id.replace(/_/g, " ") +
            "</div>" +
            rowsToHtml(f.properties) +
          "</div>"
        ).addTo(m);
      });
      m.on("mouseenter", l.id, function () { m.getCanvas().style.cursor = "pointer"; });
      m.on("mouseleave", l.id, function () { m.getCanvas().style.cursor = ""; });
    });
  };

  // Match each panel swatch's background-color to the merchant-selected
  // layer color (or a hard-coded color for the 5G layer).
  AiresInfraMap.prototype.colorizePanelSwatches = function () {
    var swatchColors = Object.assign({ fcc_5g_coverage: "#0ca678" }, this.config.colors);
    var swatches = this.container.querySelectorAll(".infra-map__swatch[data-layer]");
    for (var i = 0; i < swatches.length; i++) {
      var id = swatches[i].getAttribute("data-layer");
      if (swatchColors[id]) swatches[i].style.background = swatchColors[id];
    }
  };

  // -------------------------------------------------------------------------
  function discoverAndBoot() {
    var configs = document.querySelectorAll("script[data-infra-map-config]");
    for (var i = 0; i < configs.length; i++) {
      var raw = configs[i].textContent;
      try {
        var cfg = JSON.parse(raw);
      } catch (err) {
        console.error("[infra-map] config JSON parse failed for section",
                      configs[i].getAttribute("data-infra-map-config"), err);
        continue;
      }
      // eslint-disable-next-line no-new
      new AiresInfraMap(cfg);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", discoverAndBoot);
  } else {
    discoverAndBoot();
  }
})();
